/**
 * useCampaignRunHistory — the Runs tab's data half for the CLW Sidekick agent.
 *
 * The Campaign Grid's right-side panel used to own this behaviour: load the
 * campaign's optimize-campaign + edit-campaign messages, resume polling for the
 * in-flight ones, and dispatch per-suggestion applies. The panel is gone, and
 * this hook moves that behaviour unchanged into the launch workflow.
 *
 * Normalisation + polling mirror CampaignGridPage's loadOptimizeRuns /
 * pollOptimizeMessage; applying mirrors its applySuggestion and reuses the same
 * shared description builder so the request that reaches the platform is the
 * same one the old panel sent.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, getApiUrl } from '../utils/apiConfig';
import { OPTIMIZE_TERMINAL_STATUSES } from '../utils/optimizeCampaignRun';
import { buildSuggestionEditRequest } from '../utils/optimizerSuggestionApply';
import { findSuggestionApplication } from '../utils/suggestionApplication';

// Client-side safety net for a runaway interval — NOT the real timeout. The
// server owns that and will settle the message one way or the other. This only
// has to outlast it (see the same constant on the old grid panel).
const RUN_POLL_TIMEOUT_MS = 125 * 60 * 1000;
const RUN_POLL_INTERVAL_MS = 5000;

function toRun(m, campaignId, agentType) {
  return {
    id: m.message_id,
    campaign_id: campaignId,
    agent_type: agentType,
    status: (m.status === 'processing' || m.status === 'pending') ? 'running'
      : m.status === 'completed' ? 'success' : 'error',
    created_at: m.message_created_at || m.associated_at,
    result: m.json_response || null,
  };
}

export function useCampaignRunHistory({
  accountId,
  userId,
  campaignId,
  destination,
  // The campaign row's platform id, used when a run's result doesn't carry one
  // of its own. Null for campaigns that have not launched yet.
  platformCampaignId = null,
  // The tab is open. Nothing is fetched or polled until this is true.
  enabled = false,
}) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [messagesByCampaign, setMessagesByCampaign] = useState({});
  const [applyingKeys, setApplyingKeys] = useState(() => new Set());
  // The last apply dispatch outcome, rendered inline in the run history
  // instead of a page toast — the sidebar has no toast of its own.
  const [notice, setNotice] = useState(null); // { ts, ok, text }

  const pollTimers = useRef(new Map());
  // Monotonic request id: a stale fetch (started for a previous campaign, or
  // superseded by a newer load) must not write over the current state.
  const requestSeq = useRef(0);

  const stopPolling = useCallback(() => {
    for (const handle of pollTimers.current.values()) clearInterval(handle);
    pollTimers.current.clear();
  }, []);
  useEffect(() => stopPolling, [stopPolling]);

  // A different campaign is a clean slate. The load effect below is async;
  // without this, the previous campaign's runs and applied checks stayed on
  // screen until the new fetch landed.
  useEffect(() => {
    stopPolling();
    requestSeq.current += 1;
    setRuns([]);
    setLoading(false);
    setRefreshing(false);
    setError(null);
    setMessagesByCampaign({});
    setApplyingKeys(new Set());
    setNotice(null);
  }, [campaignId, stopPolling]);

  // ── Poll one optimize/edit message to completion ─────────────────────────
  const pollRun = useCallback((messageId) => {
    if (!messageId || pollTimers.current.has(messageId)) return;
    const startedAt = Date.now();
    const handle = setInterval(async () => {
      try {
        const r = await api.get(getApiUrl(`/api/v2/message/${messageId}/status`), {
          params: { accountId, userId },
        });
        const status = r.data?.status;
        if (OPTIMIZE_TERMINAL_STATUSES.has(status)) {
          clearInterval(handle);
          pollTimers.current.delete(messageId);
          const ok = status === 'completed';
          // Keep the message's json_response (result) on the run so the
          // expanded body can render the analysis + suggestions in place.
          setRuns(prev => prev.map(x => x.id === messageId
            ? { ...x, status: ok ? 'success' : 'error', result: r.data?.result ?? null }
            : x));
          // Mirror the terminal status into the message list too: the Applied
          // check reads that list and counts only COMPLETED edits, so without
          // this a suggestion applied in this session read as not applied
          // until a full reload — and offered Apply again.
          setMessagesByCampaign(prev => Object.fromEntries(Object.entries(prev).map(([camp, list]) => [
            camp,
            list.map(m => (m.message_id === messageId
              ? { ...m, status, json_response: r.data?.result ?? m.json_response }
              : m)),
          ])));
          return;
        }
      } catch (_) {
        // transient; keep polling until the safety timeout
      }
      if (Date.now() - startedAt > RUN_POLL_TIMEOUT_MS) {
        clearInterval(handle);
        pollTimers.current.delete(messageId);
      }
    }, RUN_POLL_INTERVAL_MS);
    pollTimers.current.set(messageId, handle);
  }, [accountId, userId]);

  // ── Hydrate from GET /campaigns/:id/messages ─────────────────────────────
  const loadRuns = useCallback(async ({ quiet = false } = {}) => {
    if (!accountId || !campaignId) return;
    const seq = ++requestSeq.current;
    if (quiet) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const r = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/messages`), {
        params: { accountId, userId },
      });
      if (seq !== requestSeq.current) return; // superseded — write nothing
      const msgs = r.data?.messages || [];
      const ofType = (type) => msgs.filter(m => m.message_type === type || m.action === type);
      const editMsgs = ofType('edit-campaign');
      // The whole list backs the "Applied" check: the matcher needs the run's
      // own suggestion as well as the edits that reference it.
      setMessagesByCampaign({ [campaignId]: msgs });
      const serverRuns = [
        ...ofType('optimize-campaign').map(m => toRun(m, campaignId, 'optimize-campaign')),
        ...editMsgs.map(m => toRun(m, campaignId, 'edit-campaign')),
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setRuns(serverRuns);
      serverRuns.filter(x => x.status === 'running').forEach(x => pollRun(x.id));
    } catch (e) {
      if (seq === requestSeq.current) setError(e?.response?.data?.error || e.message);
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [accountId, campaignId, userId, pollRun]);

  const refresh = useCallback(() => loadRuns({ quiet: true }), [loadRuns]);

  // Load whenever the tab OPENS for a campaign. Leaving the tab and returning
  // changes `enabled` back to true, so a run started meanwhile on the
  // Optimization tab appears without a manual refresh.
  useEffect(() => {
    if (!enabled || !accountId || !campaignId) return;
    loadRuns();
    // `loadRuns` is stable for a given (account, campaign, user) triple, so this
    // only re-fires on an enabled flip or an identity change — not every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, accountId, campaignId, loadRuns]);

  // ── Applied check ─────────────────────────────────────────────────────────
  // The campaign details page's matcher (utils/suggestionApplication). It reads
  // the (optimizeMessageId, suggestionIndex) tag off the USER message that
  // dispatched the edit — exposed on the bot row as source_file_metadata — and
  // counts only a COMPLETED edit made after the run. The bot row's own
  // file_metadata never carries the tag (utils/botFileMetadata.js allowlists it
  // to {frontendUrl, source}), so the old bot-row match never fired after a
  // reload and a suggestion applied yesterday offered Apply again today.
  const isSuggestionApplied = useCallback((optimizeMessageId, suggestionIndex) =>
    Boolean(findSuggestionApplication(
      messagesByCampaign[campaignId] || [], optimizeMessageId, suggestionIndex,
      { destination, platformCampaignId },
    )),
  [campaignId, messagesByCampaign, destination, platformCampaignId]);

  // ── Apply one optimizer suggestion ────────────────────────────────────────
  // Creates an edit-campaign message tagged with (optimizeMessageId,
  // suggestionIndex) so the "Applied" state survives reloads — exactly like the
  // grid panel and the campaign details page.
  const applySuggestion = useCallback(async (run, suggestion, suggestionIndex) => {
    if (!campaignId || !run) return;
    const key = `${run.id}-${suggestionIndex}`;
    setNotice(null);
    setApplyingKeys(prev => new Set(prev).add(key));
    try {
      const platformId = run.result?.details?.campaign_id
        || run.result?.campaign_id
        || platformCampaignId;
      if (!platformId) throw new Error('Platform campaign ID not found');

      const built = buildSuggestionEditRequest(suggestion, {
        destination,
        platformCampaignId: platformId,
      });
      if (built.error) throw new Error(built.error);
      const changeName = suggestion?.title || suggestion?.action_type
        || suggestion?.lever || 'this recommended change';
      if (!window.confirm(
        `Apply ${String(changeName).replace(/_/g, ' ')} to the live campaign?`,
      )) return;

      const resp = await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/edit`), {
        platformCampaignId: platformId,
        description: built.description,
        // See CampaignDetailPage: the batch path applies every edit, not just the first.
        ...(built.edits ? { edits: built.edits } : {}),
        targetEntities: built.targetEntities,
        entityContext: built.entityContext,
        suggestedValues: built.suggestedValues,
        accountId,
        userId,
        suggestionIndex,
        optimizeMessageId: run.id,
      });
      if (!resp.data?.success) throw new Error(resp.data?.error || 'Failed to create edit message');
      setNotice({ ts: Date.now(), ok: true, text: 'Edit run started' });
      // Refetch messages: the new edit-campaign row flips the card to
      // "Applied" (existence check, same as the old panel) and gets hydrated
      // as a running run with polling.
      await loadRuns({ quiet: true });
    } catch (e) {
      const text = e?.response?.data?.error || e.message;
      setNotice({ ts: Date.now(), ok: false, text });
    } finally {
      setApplyingKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  }, [accountId, campaignId, destination, platformCampaignId, userId, loadRuns]);

  return {
    runs,
    loading,
    refreshing,
    error,
    notice,
    applyingKeys,
    refresh,
    applySuggestion,
    isSuggestionApplied,
  };
}

export default useCampaignRunHistory;
